import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import StockTicker from '../components/layout/StockTicker';
import Footer from '../components/layout/Footer';
import { ArticleCardStandard } from '../components/cards/ArticleCard';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(query);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query) {
      searchArticles(query);
    }
  }, [query]);

  const searchArticles = async (q) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/articles/search/${encodeURIComponent(q)}`);
      setArticles(response.data);
    } catch (error) {
      console.error('Error searching articles:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery });
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="search-page">
      <StockTicker />
      <Navbar />

      {/* Header */}
      <header className="py-12 sm:py-16 border-b border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <h1
            className="text-4xl md:text-5xl text-[#0A192F] mb-6"
            style={{ fontFamily: "'Libre Baskerville', serif" }}
          >
            Search
          </h1>

          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" strokeWidth={1.5} />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for articles..."
                className="pl-12 py-6 text-lg bg-white border-[#E2E8F0] focus:border-[#C5A059] text-[#1A1A1A] rounded-none"
                data-testid="search-page-input"
              />
            </div>
            <Button
              type="submit"
              className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 uppercase tracking-widest text-xs px-8 rounded-none"
              data-testid="search-page-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Search'}
            </Button>
          </form>
        </div>
      </header>

      {/* Results */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          {query && (
            <p className="text-[#64748B] mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {loading ? (
                'Searching...'
              ) : (
                <>
                  Found <span className="text-[#0A192F] font-semibold">{articles.length}</span> results for "
                  <span className="text-[#C5A059]">{query}</span>"
                </>
              )}
            </p>
          )}

          {loading ? (
            <div className="space-y-5">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="w-full h-[140px] sm:h-[180px] rounded-none" />
              ))}
            </div>
          ) : articles.length > 0 ? (
            <div className="space-y-5">
              {articles.map((article) => (
                <ArticleCardStandard key={article.id} article={article} />
              ))}
            </div>
          ) : query ? (
            <div className="text-center py-20">
              <p className="text-[#64748B] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>No articles found for "{query}"</p>
              <p className="text-[#64748B]/60 text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Try different keywords or browse our categories</p>
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-[#64748B]" style={{ fontFamily: "'Outfit', sans-serif" }}>Enter a search term to find articles</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
